import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StepperFormWrapper from "../../../lib/StepperFormWrapper";
import { StepperType } from "../../../lib/StepperWrapper";
import { ScreenTitle } from "../../../lib/typography";
import VolumeVisualizer from "../VolumeVisualizer.tsx";

type Props = {
  stepper: StepperType;
  onSubmit: (microphoneOk: boolean) => void | Promise<void>;
};

enum MediaErrorTypes {
  NO_DEVICES = "no_devices",
  PERMISSION = "permission",
  GENERIC_ERROR = "error",
  NO_ERROR = "",
}

export default function TestMicrophoneStep({ stepper, onSubmit }: Props) {
  const { t } = useTranslation();

  const [audioLevel, setAudioLevel] = useState(0);
  const [stream, setStream] = useState<MediaStream>();
  const [mediaError, setMediaError] = useState<MediaErrorTypes>(
    MediaErrorTypes.NO_ERROR
  );

  useEffect(() => {
    let disposed = false;
    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let dataArray: Uint8Array<ArrayBuffer> | null = null;
    let rafId: number | null = null;
    let smoothedVolume = 0;
    const SMOOTHING_FACTOR = 0.95;
    const DEBOUNCE_DELAY = 50;
    let lastTime = 0;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (disposed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        setStream(stream);
        setMediaError(MediaErrorTypes.NO_ERROR);
        setupAudioAnalysis(stream);
      })
      .catch((err: unknown) => {
        if (disposed) return;
        const errorMessage = String(err);
        if (errorMessage.includes("NotAllowedError")) {
          setMediaError(MediaErrorTypes.PERMISSION);
        } else if (errorMessage.includes("NotFoundError")) {
          setMediaError(MediaErrorTypes.NO_DEVICES);
        } else {
          setMediaError(MediaErrorTypes.GENERIC_ERROR);
        }
      });

    function setupAudioAnalysis(stream: MediaStream) {
      try {
        audioContext = new window.AudioContext();
        const source = audioContext.createMediaStreamSource(stream);

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        const bufferLength = analyser.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);

        source.connect(analyser);

        const analyzeAudio = () => {
          if (analyser && dataArray) {
            analyser.getByteTimeDomainData(dataArray);

            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
              sum += (dataArray[i] - 128) ** 2;
            }
            const currentVolume = Math.sqrt(sum / dataArray.length);

            // Apply smoothing
            smoothedVolume =
              currentVolume * (1 - SMOOTHING_FACTOR) +
              smoothedVolume * SMOOTHING_FACTOR;

            const now = Date.now();
            if (now - lastTime > DEBOUNCE_DELAY) {
              setAudioLevel(Math.max(0, smoothedVolume - 0.8));
              lastTime = now;
            }
          }
          rafId = requestAnimationFrame(analyzeAudio);
        };

        analyzeAudio();
      } catch (err) {
        console.error("Error setting up audio analysis:", err);
        setMediaError(MediaErrorTypes.GENERIC_ERROR);
      }
    }

    return () => {
      disposed = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, []);

  const stopStream = useCallback(() => {
    stream?.getTracks().forEach((track) => {
      track.stop();
    });
  }, [stream]);

  useEffect(
    () => () => {
      stopStream();
    },
    [stopStream]
  );

  return (
    <StepperFormWrapper
      stepper={stepper({})}
      handleSubmit={() => {
        onSubmit(!mediaError);
      }}
    >
      <ScreenTitle>{t("Check microphone")}</ScreenTitle>
      <Stack
        sx={{
          height: "calc(100vh - 400px)",
          mt: 4,
          mb: 6,
        }}
        spacing={3}
      >
        <Box>{t("Does the mic indicator change when you make a sound?")}</Box>
        <Box sx={{ flexGrow: 1 }} />
        {mediaError && (
          <Alert severity="error" sx={{ mt: 4, alignItems: "flex-start" }}>
            <AlertTitle>
              {mediaError === MediaErrorTypes.PERMISSION
                ? t("Microphone access is not allowed")
                : mediaError === MediaErrorTypes.NO_DEVICES
                  ? t("Could not find a microphone")
                  : t("Could not access the microphone.")}
            </AlertTitle>
            <Typography variant="body2">
              {mediaError === MediaErrorTypes.PERMISSION
                ? t(
                    "Please change your site permissions to allow Connect to use the microphone."
                  )
                : mediaError === MediaErrorTypes.NO_DEVICES
                  ? t("Please ensure you have a microphone connected.")
                  : t(
                      "An error occurred while trying to access the microphone."
                    )}
            </Typography>
          </Alert>
        )}
        <VolumeVisualizer volume={audioLevel} activeMicrophone={!mediaError} />
        <Box sx={{ flexGrow: 1 }} />
      </Stack>
    </StepperFormWrapper>
  );
}
